import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  ButtonModule,
  CommonMediaQueries,
  DIALOG_DATA,
  DropdownModule,
  equals,
  IconModule,
  MediaScreenService,
} from '@hglobal/fin-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { TableComponent, TitleComponent } from '@iw/common';
import { DialogData, Operation, OperationStatus } from '@iw/core/entities';
import { IwDecimalPipe } from '@iw/core/pipes';
import { IwDialogService } from '@iw/core/services';
import { environment } from '@iw/environment';
import { WithdrawalComponent } from '@iw/withdrawal';

import { ORDERS_HISTORY_API, OrdersHistoryApiService } from './api';
import { CancelConfirmDialogComponent } from './components/cancel-confirm-dialog/cancel-confirm-dialog.component';
import { DetailsComponent } from './components/details/details.component';
import {
  OrderDetailsDialogData,
  TAB,
} from './components/details/details.entities';
import { OrdersFiltersComponent } from './components/filters/filters.component';
import { OrdersHistoryMockApiService } from './mocks';
import {
  ONGOING_ORDERS_COLUMNS,
  ORDERS_COLUMNS,
} from './orders-history-columns';
import { initialFilters, OrdersHistoryStore } from './orders-history.store';
import { ButtonComponent } from '../../common/button/button.component';
import { StatusCardComponent } from '../../common/status-card/status-card.component';

@Component({
  selector: 'iw-orders-history',
  styleUrl: './orders-history.component.scss',
  templateUrl: './orders-history.component.html',
  imports: [
    DatePipe,
    TableComponent,
    TranslateModule,
    StatusCardComponent,
    IconModule,
    ButtonComponent,
    TitleComponent,
    OrdersFiltersComponent,
    DropdownModule,
    ButtonModule,
    IwDecimalPipe,
  ],
  providers: [
    OrdersHistoryStore,
    {
      provide: ORDERS_HISTORY_API,
      useClass: environment.useMocks
        ? OrdersHistoryMockApiService
        : OrdersHistoryApiService,
    },
  ],
})
export class OrdersHistoryComponent {
  readonly #dialogService = inject(IwDialogService);
  readonly #translate = inject(TranslateService);
  readonly #store = inject(OrdersHistoryStore);
  readonly #injector = inject(Injector);
  readonly #mediaScreenService = inject(MediaScreenService);
  readonly #dialog =
    inject<DialogData<OrdersHistoryComponent, void>>(DIALOG_DATA);

  readonly columns = ORDERS_COLUMNS;
  readonly gridTemplateColumns = '1fr 1fr 1fr 1fr 1fr 1fr 1fr';
  readonly ongoingOrdersColumns = ONGOING_ORDERS_COLUMNS;
  readonly OperationStatus = OperationStatus;

  readonly orders = this.#store.orders;
  readonly filters = this.#store.filters;
  readonly ongoingOrders = this.#store.ongoingOrders;
  readonly ongoingFilters = this.#store.ongoingFilters;
  readonly loadingOrders = this.#store.isGetOrdersPending;
  readonly cancelPending = this.#store.isCancelOrderPending;
  readonly loadingOngoingOrders = this.#store.isGetOngoingOrdersPending;

  readonly isDesktop = toSignal(
    this.#mediaScreenService.mediaMatcher(CommonMediaQueries.MD),
  );

  readonly initializing = computed(() => {
    const loading = this.loadingOrders() || this.loadingOngoingOrders();
    return loading && !(this.orders() && this.ongoingOrders());
  });

  readonly showOrders = computed(() => {
    const orders = this.orders();
    const loading = this.loadingOrders();
    const emptyView = !this.ongoingOrders()?.items?.length && this.isDesktop();
    return !!(
      orders &&
      (orders?.items?.length || this.filtersDirty || loading || emptyView)
    );
  });

  readonly showFilters = computed(() => {
    return (
      this.isDesktop() &&
      !!(
        this.loadingOrders() ||
        (this.filtersDirty && !this.orders()?.items?.length) ||
        this.orders()?.items?.length
      )
    );
  });

  cancelEffect = effect(() => {
    if (this.#store.isCancelOrderSuccess()) {
      untracked(() => {
        if (!this.#store.order()) this.#store.resetRequestStatus('cancelOrder');
      });
    }
  });

  get dialogData() {
    return this.#dialog.data;
  }

  get filtersDirty(): boolean {
    return !equals(initialFilters, this.filters());
  }

  openDetails(params: { id: string; openMessages?: boolean }): void {
    const { id, openMessages } = params;
    this.#dialogService.open<DetailsComponent, OrderDetailsDialogData>(
      {
        components: [DetailsComponent],
        data: {
          id,
          tab: openMessages ? TAB.MESSAGES : TAB.DESCRIPTION,
        },
        fullScreen: true,
      },
      this.#injector,
    );
  }

  openWithdrawal(): void {
    this.#dialogService.open<WithdrawalComponent, void>({
      title: this.#translate.instant('withdrawal.dialog.title'),
      components: [WithdrawalComponent],
      fullScreen: true,
    });
  }

  loadMore(): void {
    const next = this.orders()?.cursor;
    this.#store.updateFilters({ cursor: next });
  }

  loadMoreOngoing(): void {
    const next = this.ongoingOrders()?.cursor;
    this.#store.update({
      ongoingFilters: { ...this.ongoingFilters(), cursor: next },
    });
  }

  openFiltersDialog() {
    this.#dialogService.open<OrdersFiltersComponent, { isDialogView: boolean }>(
      {
        components: [OrdersFiltersComponent],
        title: this.#translate.instant('ordersHistory.filters.title'),
        data: { isDialogView: true },
        fullScreen: true,
      },
      this.#injector,
    );
  }

  openCancelConfirm(order: Operation): void {
    this.#dialogService
      .open<CancelConfirmDialogComponent, void>({
        components: [CancelConfirmDialogComponent],
      })
      .afterClosed<boolean>()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.#store.cancelOrder({ id: order.id });
      });
  }
}
